/**
 * Site feature-flag hooks.
 *
 * One React Query cache entry (`["site-feature-flags"]`) holds the public
 * payload `{ featureFlags, version }` from `GET /api/v1/core/feature-flags/`.
 * It governs *exposure* ("does this product surface exist here?") — distinct
 * from the payment/BYOK *entitlement* answered by `usePaywall` /
 * `useAIFeatureEnabled`. Both must pass for a surface to render.
 *
 * - Seeded from the SSR `window.__FEATURE_FLAGS__` bootstrap, so first paint
 *   never flashes a disabled link.
 * - Revalidated on focus and after 30 s with a conditional GET (`ETag` /
 *   `If-None-Match`); an unchanged snapshot costs a 304.
 * - Fail closed: a feature is enabled only when the map says exactly `true`.
 *   While loading without a bootstrap every feature reads as disabled.
 */

import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  isFeatureOn,
  normalizeFeatureFlagsPayload,
  type SiteFeatureFlags,
  type SiteFeatureFlagsPayload,
} from "@shared/lib/site-features";

export type { SiteFeatureFlags, SiteFeatureFlagsPayload };

export const FEATURE_FLAGS_ENDPOINT = "/api/v1/core/feature-flags/";
export const FEATURE_FLAGS_STALE_TIME_MS = 30_000;

export const siteFeatureFlagKeys = {
  all: ["site-feature-flags"] as const,
};

export function readBootstrappedFeatureFlagsPayload(): SiteFeatureFlagsPayload | null {
  if (typeof window === "undefined") return null;
  return normalizeFeatureFlagsPayload(
    (window as unknown as { __FEATURE_FLAGS__?: unknown }).__FEATURE_FLAGS__,
  );
}

/** Legacy accessor: the bootstrapped flag map (or `null`). */
export function readBootstrappedFeatureFlags(): SiteFeatureFlags | null {
  return readBootstrappedFeatureFlagsPayload()?.featureFlags ?? null;
}

let lastEtag: string | null = null;

/**
 * Fetch the payload with a conditional GET. On `304` the cached payload is
 * reused; any failure keeps whatever is cached (or throws when nothing is),
 * so a flaky network never turns hidden features back on.
 */
export async function fetchSiteFeatureFlags(
  queryClient?: QueryClient,
): Promise<SiteFeatureFlagsPayload> {
  const cached =
    queryClient?.getQueryData<SiteFeatureFlagsPayload>(siteFeatureFlagKeys.all) ?? null;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (cached && lastEtag) headers["If-None-Match"] = lastEtag;

  const response = await fetch(FEATURE_FLAGS_ENDPOINT, {
    credentials: "include",
    headers,
  });
  if (response.status === 304 && cached) return cached;
  if (!response.ok) {
    if (cached) return cached;
    throw new Error(`feature flags: HTTP ${response.status}`);
  }
  lastEtag = response.headers.get("ETag");
  const payload = normalizeFeatureFlagsPayload(await response.json());
  if (!payload) throw new Error("feature flags: malformed payload");
  return payload;
}

/** Test helper: forget the remembered ETag. */
export function resetFeatureFlagsEtag(): void {
  lastEtag = null;
}

const selectFlags = (payload: SiteFeatureFlagsPayload): SiteFeatureFlags =>
  payload.featureFlags;

/** The effective flag map (`data`) plus the usual query state. */
export function useSiteFeatureFlags() {
  const queryClient = useQueryClient();
  return useQuery<SiteFeatureFlagsPayload, Error, SiteFeatureFlags>({
    queryKey: siteFeatureFlagKeys.all,
    queryFn: () => fetchSiteFeatureFlags(queryClient),
    initialData: readBootstrappedFeatureFlagsPayload() ?? undefined,
    // Bootstrap is fresh as of the page render; revalidate after the window.
    initialDataUpdatedAt: () => Date.now(),
    select: selectFlags,
    staleTime: FEATURE_FLAGS_STALE_TIME_MS,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

/** O(1) lookup. `enabled` is `false` while loading with no bootstrap. */
export function useFeatureEnabled(featureKey: string): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useSiteFeatureFlags();
  return {
    enabled: isFeatureOn(data, featureKey),
    isLoading: isLoading && data === undefined,
  };
}

/** @deprecated Use {@link useFeatureEnabled}; kept for existing call sites. */
export const useSiteFeatureEnabled = useFeatureEnabled;
